import { m } from "framer-motion";
import type { ReactNode } from "react";
import {
  Cancel01Icon,
  CentralIcon,
  Tick02Icon,
} from "~/components/icons/central-icons";
import { STATUS_SPHERE_PALETTE } from "~/components/status-sphere-palette";
import { fluidSprings } from "~/components/ui/fluid-motion";

interface StatusDotProps {
  /** Solid sphere base color. */
  baseColor: string;
  /** Hue-blended tint layered over the gloss. */
  tintColor: string;
  icon: ReactNode;
  iconInitialRotate?: number;
}

/**
 * Glossy status sphere (ported from v1's toast status dots): a colored ball
 * with a radial highlight and a hue-blended tint, popping in with its icon.
 * The literal colors are deliberate — the spheres read identically in light
 * and dark themes.
 */
function StatusDot({
  baseColor,
  tintColor,
  icon,
  iconInitialRotate = 0,
}: StatusDotProps) {
  return (
    <m.div
      animate={{ scale: 1, opacity: 1 }}
      className="relative isolate flex h-8 w-8 items-center justify-center overflow-hidden rounded-full"
      initial={{ scale: 0.5, opacity: 0 }}
      transition={fluidSprings.moderate}
    >
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(100% 100% at 30% 18%, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0) 58%), linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(0, 0, 0, 0.06) 100%), ${baseColor}`,
        }}
      />
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{ background: tintColor, mixBlendMode: "hue" }}
      />
      <m.div
        animate={{ scale: 1, rotate: 0 }}
        className="relative z-10"
        initial={{ scale: 0, rotate: iconInitialRotate }}
        transition={{ ...fluidSprings.moderate, delay: 0.1 }}
      >
        {icon}
      </m.div>
    </m.div>
  );
}

export function SuccessStatusDot() {
  return (
    <StatusDot
      baseColor={STATUS_SPHERE_PALETTE.success.base}
      icon={<CentralIcon className="size-4 text-white" icon={Tick02Icon} />}
      iconInitialRotate={-20}
      tintColor={STATUS_SPHERE_PALETTE.success.tint}
    />
  );
}

export function ErrorStatusDot() {
  return (
    <StatusDot
      baseColor={STATUS_SPHERE_PALETTE.error.base}
      icon={<CentralIcon className="size-4 text-white" icon={Cancel01Icon} />}
      iconInitialRotate={45}
      tintColor={STATUS_SPHERE_PALETTE.error.tint}
    />
  );
}

export function WarningStatusDot() {
  return (
    <StatusDot
      baseColor={STATUS_SPHERE_PALETTE.warning.base}
      icon={
        <span className="text-meta leading-none font-semibold text-white">!</span>
      }
      iconInitialRotate={-20}
      tintColor={STATUS_SPHERE_PALETTE.warning.tint}
    />
  );
}
